/** Shapes returned by the API that the UI relies on (mirrors apps/api). */

export type Role = "admin" | "auditor";
export type Locale = "hy" | "ru" | "en";

export interface AuthUser {
  id: number;
  username: string;
  displayName: string;
  role: Role;
  locale: string;
  companyIds: number[] | null;
}

export interface Company {
  id: number;
  code: string;
  nameHy: string;
  nameRu: string | null;
  nameEn: string | null;
  mulberryBaseUrl: string | null;
  cloudLlmAllowed: boolean;
  /** Inactive companies are listed too (their documents keep a name); pickers filter on this. */
  active: boolean;
}

export interface DocType {
  id: number;
  code: string;
  nameHy: string;
  nameRu: string | null;
  nameEn: string | null;
}

export interface Lookups {
  companies: Company[];
  docTypes: DocType[];
  embeddingModel: string;
  /** Local model id when answers are enabled, else null. */
  llm: string | null;
  /** Optional modules and whether each is switched on (see @audit/shared MODULE_IDS). */
  modules: Record<ModuleId, boolean>;
  roles: Role[];
}

export type ModuleId =
  | "llm"
  | "upload"
  | "inbox"
  | "csv-import"
  | "export"
  | "eval"
  | "rerank"
  | "delete"
  | "mulberry"
  | "detect"
  | "search-log";

export interface SummarySource {
  n: number;
  chunkId: number;
  documentId: number;
  title: string;
  docType: string | null;
  regNumber: string | null;
  docDate: string | null;
  pageNo: number;
  clause: string | null;
  text: string;
}

/** A sentence picked from a source as evidence; verified = the separate check confirmed it. */
export interface EvidenceSentence {
  label: string;
  n: number;
  /** Other sources with the same sentence. */
  also: number[];
  text: string;
  verified: boolean | null;
}

export interface Summary {
  id: number;
  status: "queued" | "running" | "done" | "failed";
  /** Which model call is running: picking evidence, checking it, writing the answer. */
  phase: "pick" | "verify" | "answer" | null;
  query: string;
  model: string;
  sources: SummarySource[];
  evidence: EvidenceSentence[];
  text: string;
  dropped: number;
  unsupported: number;
  tookMs: number | null;
  error: string | null;
  createdAt: string;
  updatedAt: string;
}

/** Localized name of a company / type for the current UI language. */
export function localizedName(
  item: { nameHy: string; nameRu: string | null; nameEn: string | null },
  locale: string,
): string {
  if (locale === "ru" && item.nameRu) return item.nameRu;
  if (locale === "en" && item.nameEn) return item.nameEn;
  return item.nameHy;
}

// ---- Search ---------------------------------------------------------------------------------

export type SearchMode = "hybrid" | "fts" | "semantic" | "exact";
export type SearchDepth = "normal" | "deep";

export interface Fragment {
  chunkId: number;
  pageNo: number;
  snippetHtml: string;
  charStart: number;
  charEnd: number;
  lexical: boolean;
  semantic: boolean;
  score: number;
  clause?: string;
}

export interface DocumentSummary {
  id: number;
  title: string;
  regNumber: string | null;
  docDate: string | null;
  companyId: number;
  docTypeId: number | null;
  pageCount: number;
  textQuality: number | null;
  status: string;
  ocrPages: number;
}

export interface SearchGroup {
  document: DocumentSummary;
  docScore: number;
  hitCount: number;
  fragments: Fragment[];
}

export interface SearchResponse {
  searchId: number;
  query: {
    text: string;
    words: string[];
    phrases: string[];
    negated: string[];
    regNumber?: string;
  };
  docsTotal: number;
  page: number;
  pageSize: number;
  groups: SearchGroup[];
  documentsByNumber: DocumentSummary[];
  facets: {
    companies: { id: number; count: number }[];
    types: { id: number | null; count: number }[];
    years: { year: number | null; count: number }[];
  };
  highlightTerms: string[];
  timings: { ftsMs: number; knnMs: number; totalMs: number };
}

export interface DocumentHits {
  documentId: number;
  query: SearchResponse["query"];
  highlightTerms: string[];
  hitCount: number;
  fragments: Fragment[];
}

// ---- Documents ------------------------------------------------------------------------------

export interface DocumentVersion {
  id: number;
  version: number;
  status: string;
  doc_date: string | null;
  created_at: string;
}

export interface IngestionEvent {
  stage: string;
  level: string;
  message: string | null;
  data: unknown;
  duration_ms: number | null;
  created_at: string;
}

export interface DocumentCard extends DocumentSummary {
  originalName: string;
  ext: string | null;
  mime: string | null;
  sizeBytes: number;
  lang: string | null;
  version: number;
  supersedesId: number | null;
  sourceKind: string;
  sourceRef: string | null;
  mulberryId: string | null;
  mulberryUrl: string | null;
  meta: Record<string, unknown>;
  metadataConfidence: number | null;
  error: string | null;
  legacyPages: number;
  createdAt: string;
  updatedAt: string | null;
  indexedAt: string | null;
  hasFile: boolean;
  hasPdf: boolean;
  /** Where the file lives in folder sources (Windows path when the source has a prefix). */
  locations: {
    sourceId: number;
    sourceName: string;
    relPath: string;
    path: string;
    present: boolean;
  }[];
  /** Why the file cannot be shown: removed | source_disabled; null when it can. */
  fileProblem: string | null;
  pages: { pages: number; ocr: number; legacy: number };
  company: {
    id: number;
    code: string;
    name_hy: string;
    name_ru: string | null;
    name_en: string | null;
  } | null;
  docType: {
    id: number;
    code: string;
    name_hy: string;
    name_ru: string | null;
    name_en: string | null;
  } | null;
  versions: DocumentVersion[];
  events: IngestionEvent[];
}

/** OCR word box: [text, x0, y0, x1, y1, confidence], coordinates normalized 0..1. */
export type WordBox = [string, number, number, number, number, number];

export interface PageData {
  documentId: number;
  pageNo: number;
  text: string;
  source: "text_layer" | "legacy_map" | "ocr" | "none";
  quality: number | null;
  width: number | null;
  height: number | null;
  rotation: number;
  words: WordBox[] | null;
  layout: unknown;
}

export interface DocumentPatch {
  title?: string;
  regNumber?: string | null;
  docDate?: string | null;
  companyId?: number;
  docTypeId?: number | null;
}
